import { faAddressBook, faFileLines, faImages } from '@fortawesome/free-regular-svg-icons';
import { faRightLeft, faGaugeHigh } from '@fortawesome/free-solid-svg-icons';
import {
  faAd,
  faChartLine,
  faDollar,
  faFileArrowDown,
  faLink,
  faPhotoFilm,
  faSheetPlastic,
  faSuitcase,
  faTable,
  faTags,
  faUserTie,
  faChartColumn
} from '@fortawesome/free-solid-svg-icons';
import { lazy } from 'react';
import { Button, FaIcon } from '../components';
import type { RouteItemChildWithPath } from './utils';

const authRoutes: RouteItemChildWithPath[] = [
  {
    name: 'Login',
    component: lazy(() => import(/* webpackChunkName: "Login" */ '../views/auth/Login')),
    path: '/login',
  },
];

const adminRoutes: RouteItem[] = [
  {
    name: 'Advertisers',
    path: '/advertisers',
    icon: <FaIcon icon={faAd} />,
    component: lazy(
      () =>
        import(/* webpackChunkName: "ListAdvertisers" */ '../views/apps/Advertiser/ListAdvertiser'),
    ),
    buttons: [
      ({ openDrawer }) => (
        <Button.Create onClick={() => openDrawer('Advertiser')}>Add Advertiser</Button.Create>
      ),
    ],
  },
  {
    name: 'Exchanges',
    path: '/exchanges',
    icon: <FaIcon icon={faRightLeft} />,
    component: lazy(
      () => import(/* webpackChunkName: "ListExchange" */ '../views/apps/Exchange/ListExchange'),
    ),
    buttons: [
      ({ openDrawer }) => (
        <Button.Create onClick={() => openDrawer('Exchange')}>Add Exchange</Button.Create>
      ),
    ],
  },
  {
    name: 'Rate Limit',
    path: '/rate-limit',
    icon: <FaIcon icon={faGaugeHigh} />,
    component: lazy(
      () => import(/* webpackChunkName: "ListRateLimit" */ '../views/apps/RateLimit/ListRateLimit'),
    ),
    buttons: [
      ({ openDrawer }) => (
        <Button.Create onClick={() => openDrawer('RateLimit')}>Add Rate Limit</Button.Create>
      ),
    ],
  },
  {
    name: 'Users',
    path: '/users',
    icon: <FaIcon icon={faUserTie} />,
    component: lazy(
      () => import(/* webpackChunkName: "ListUsers" */ '../views/apps/User/ListUser'),
    ),
    buttons: [
      ({ openDrawer }) => (
        <Button.Create onClick={() => openDrawer('User')}>Add User</Button.Create>
      ),
    ],
  },
  {
    name: 'Funds',
    path: '/funds',
    icon: <FaIcon icon={faDollar} />,
    component: lazy(
      () => import(/* webpackChunkName: "ListFunds" */ '../views/apps/Fund/ListFund'),
    ),
    buttons: [
      ({ openDrawer }) => (
        <Button.Create onClick={() => openDrawer('Fund')}>Add Credit Activity</Button.Create>
      ),
    ],
  },
];

const managerRoutes: RouteItem[] = [
  {
    name: 'Dashboard',
    path: '/dashboard',
    icon: <FaIcon icon={faChartLine} />,
    component: lazy(
      () => import(/* webpackChunkName: "ViewDahboard" */ '../views/apps/Dashboard/ViewDashboard'),
    ),
    showTitle: false,
  },
  {
    name: 'Report',
    path: '/report',
    icon: <FaIcon icon={faTable} />,
    views: [
      {
        name: 'Campaign Report',
        path: '/campaign',
        icon: <FaIcon icon={faTable} />,
        component: lazy(
          () =>
            import(/* webpackChunkName: "ListCustomStats" */ '../views/apps/Stats/ListCustomStats'),
        ),
      },
      {
        name: 'Exported CSV',
        path: '/app',
        icon: <FaIcon icon={faFileArrowDown} />,
        component: lazy(
          () =>
            import(
              /* webpackChunkName: "ListReportDownloads" */ '../views/apps/AppReport/ListReportDownloads'
            ),
        ),
        // invisible: true,
      },
    ],
  },

  {
    name: 'Campaigns',
    path: '/campaigns',
    icon: <FaIcon icon={faTags} />,
    component: lazy(
      () => import(/* webpackChunkName: "ListCampaigns" */ '../views/apps/Campaign/ListCampaign'),
    ),
    buttons: [() => <Button.Create href={'/campaigns/create'}>Add Campaign</Button.Create>],
  },
  {
    name: 'Add Campaign',
    path: '/campaigns/create',
    component: lazy(
      () => import(/* webpackChunkName "AddCampaign" */ '../views/apps/Campaign/AddCampaign'),
    ),
    invisible: true,
  },
  {
    name: 'Edit Campaign',
    path: '/campaigns/edit/:id',
    component: lazy(
      () => import(/* webpackChunkName "AddCampaign" */ '../views/apps/Campaign/AddCampaign'),
    ),
    exact: true,
    invisible: true,
  },
  {
    name: 'View Campaign',
    path: '/campaigns/view/:id',
    component: lazy(
      () => import(/* webpackChunkName "ViewCampaign" */ '../views/apps/Campaign/ViewCampaign'),
    ),
    exact: true,
    invisible: true,
    showTitle: false,
  },
  {
    name: 'Preview Campaign',
    path: '/campaigns/preview/:id',
    component: lazy(
      () =>
        import(
          /* webpackChunkName: "CampaignPreview" */ '../views/apps/Campaign/Preview/CampaignPreview'
        ),
    ),
    exact: true,
    invisible: true,
    showTitle: false,
  },
  {
    name: 'Creatives',
    path: '/creatives',
    icon: <FaIcon icon={faPhotoFilm} />,
    views: [
      {
        name: 'Creatives',
        path: '',
        icon: <FaIcon icon={faImages} />,
        component: lazy(
          () =>
            import(/* webpackChunkName: "ListCreatives" */ '../views/apps/Creative/ListCreative'),
        ),
        buttons: [() => <Button.Create href={'/creatives/create'}>Add Creative</Button.Create>],
      },
      {
        name: 'Add Creative',
        path: '/create',
        component: lazy(
          () => import(/* webpackChunkName "AddCreative" */ '../views/apps/Creative/AddCreative'),
        ),
        invisible: true,
      },
      {
        name: 'Edit Creative',
        path: '/edit/:id',
        component: lazy(
          () => import(/* webpackChunkName "AddCreative" */ '../views/apps/Creative/AddCreative'),
        ),
        exact: true,
        invisible: true,
      },
      {
        name: 'Trackers',
        path: '/trackers',
        icon: <FaIcon icon={faLink} />,
        component: lazy(
          () =>
            import(
              /* webpackChunkName: "ListTracker" */ '../views/apps/Creative/Tracker/ListTracker'
            ),
        ),
        buttons: [
          ({ openDrawer }) => (
            <Button.Create onClick={() => openDrawer('Tracker')}>Add Tracker</Button.Create>
          ),
        ],
      },
    ],
  },
  {
    name: 'Orders',
    path: '/orders',
    icon: <FaIcon icon={faSuitcase} />,
    component: lazy(
      () => import(/* webpackChunkName: "ListOrders" */ '../views/apps/Order/ListOrder'),
    ),
    buttons: [
      ({ openDrawer }) => (
        <Button.Create onClick={() => openDrawer('Order')}>Add Order</Button.Create>
      ),
    ],
  },
  {
    name: 'View Order',
    path: '/orders/view/:id',
    component: lazy(
      () => import(/* webpackChunkName "ViewOrder" */ '../views/apps/Order/ViewOrder'),
    ),
    exact: true,
    invisible: true,
    showTitle: false,
  },
  {
    name: 'App Lists',
    path: '/app-lists',
    icon: <FaIcon icon={faFileLines} />,
    component: lazy(
      () => import(/* webpackChunkName: "ListAppLists" */ '../views/apps/AppList/ListAppList'),
    ),
    buttons: [
      ({ openDrawer }) => (
        <Button.Create onClick={() => openDrawer('AppList')}>Add App List</Button.Create>
      ),
    ],
  },
  {
    name: 'View App List',
    path: '/app-lists/view/:id',
    component: lazy(
      () => import(/* webpackChunkName "ViewAppList" */ '../views/apps/AppList/ViewAppList'),
    ),
    exact: true,
    invisible: true,
    showTitle: false,
  },
  {
    name: 'Audience',
    path: '/audiences',
    icon: <FaIcon icon={faAddressBook} />,
    component: lazy(
      () => import(/* webpackChunkName: "ListAudiences" */ '../views/apps/Audience/ListAudience'),
    ),
    buttons: [
      ({ openDrawer }) => (
        <Button.Create onClick={() => openDrawer('Audience')}>Add 1st PARTY Audience</Button.Create>
      ),
      ({ openDrawer }) => (
        <Button.Create onClick={() => openDrawer('CustomAudience')}>
          Add Custom Audience
        </Button.Create>
      ),
    ],
  },
  {
    name: '1st PARTY Audience',
    path: '/audiences/view/:id',
    component: lazy(
      () => import(/* webpackChunkName "ViewAudience" */ '../views/apps/Audience/ViewAudience'),
    ),
    exact: true,
    invisible: true,
    showTitle: false,
  },
  {
    name: 'Custom Audience',
    path: '/audiences/view/:id',
    component: lazy(
      () => import(/* webpackChunkName "ViewAudience" */ '../views/apps/Audience/ViewAudience'),
    ),
    exact: true,
    invisible: true,
    showTitle: false,
  },
  {
    name: 'Ip Lists',
    path: '/ip-lists',
    icon: <FaIcon icon={faSheetPlastic} />,
    component: lazy(
      () => import(/* webpackChunkName: "ListIp Lists" */ '../views/apps/IpList/ListIpList'),
    ),
    buttons: [
      ({ openDrawer }) => (
        <Button.Create onClick={() => openDrawer('IpList')}>Add IP List</Button.Create>
      ),
    ],
  },
  {
    name: 'View Ip List',
    path: '/ip-lists/view/:id',
    component: lazy(
      () => import(/* webpackChunkName "ViewIpList" */ '../views/apps/IpList/ViewIpList'),
    ),
    exact: true,
    invisible: true,
    showTitle: false,
  },
  {
    name:'Inventory',
    path:'/inventory',
    icon: <FaIcon icon={faChartColumn} />,
    component:lazy(
      ()=>import(/* webpackChunkName "ListInventory" */ '../views/apps/Inventory/ListInventory')
    )
  },
  {
    name:'Inventory Bids Wins',
    path:'/inventory/bids-wins',
    icon: <FaIcon icon={faChartColumn} />,
    component:lazy(
      ()=>import(/* webpackChunkName "ListInventory2" */ '../views/apps/Inventory2/ListInventory2')
    )
  },
];

const analystRoutes: RouteItem[] = [
  {
    name: 'Dashboard',
    path: '/dashboard',
    icon: <FaIcon icon={faChartLine} />,
    component: lazy(
      () => import(/* webpackChunkName: "ViewDahboard" */ '../views/apps/Dashboard/ViewDashboard'),
    ),
    showTitle: false,
  },
  {
    name: 'Report',
    path: '/report',
    icon: <FaIcon icon={faTable} />,
    views: [
      {
        name: 'Campaign Report',
        path: '/campaign',
        icon: <FaIcon icon={faTable} />,
        component: lazy(
          () =>
            import(/* webpackChunkName: "ListCustomStats" */ '../views/apps/Stats/ListCustomStats'),
        ),
      },
      {
        name: 'Exported CSV',
        path: '/app',
        icon: <FaIcon icon={faFileArrowDown} />,
        component: lazy(
          () =>
            import(
              /* webpackChunkName: "ListReportDownloads" */ '../views/apps/AppReport/ListReportDownloads'
            ),
        ),
        // invisible: true,
      },
    ],
  },
  {
    name: 'Campaigns',
    path: '/campaigns',
    icon: <FaIcon icon={faTags} />,
    component: lazy(
      () => import(/* webpackChunkName: "ListCampaigns" */ '../views/apps/Campaign/ListCampaign'),
    ),
  },
  {
    name: 'Creatives',
    path: '/creatives',
    icon: <FaIcon icon={faPhotoFilm} />,
    views: [
      {
        name: 'Creatives',
        path: '',
        icon: <FaIcon icon={faImages} />,
        component: lazy(
          () =>
            import(/* webpackChunkName: "ListCreatives" */ '../views/apps/Creative/ListCreative'),
        ),
      },
      {
        name: 'Trackers',
        path: '/trackers',
        icon: <FaIcon icon={faLink} />,
        component: lazy(
          () =>
            import(
              /* webpackChunkName: "ListTracker" */ '../views/apps/Creative/Tracker/ListTracker'
            ),
        ),
      },
    ],
  },
  {
    name: 'Orders',
    path: '/orders',
    icon: <FaIcon icon={faSuitcase} />,
    component: lazy(
      () => import(/* webpackChunkName: "ListOrders" */ '../views/apps/Order/ListOrder'),
    ),
  },
  {
    name: 'App Lists',
    path: '/app-lists',
    icon: <FaIcon icon={faFileLines} />,
    component: lazy(
      () => import(/* webpackChunkName: "ListAppLists" */ '../views/apps/AppList/ListAppList'),
    ),
  },
  {
    name: 'Audience',
    path: '/audiences',
    icon: <FaIcon icon={faAddressBook} />,
    component: lazy(
      () => import(/* webpackChunkName: "ListAudiences" */ '../views/apps/Audience/ListAudience'),
    ),
  },
  {
    name: 'Ip Lists',
    path: '/ip-lists',
    icon: <FaIcon icon={faSheetPlastic} />,
    component: lazy(
      () => import(/* webpackChunkName: "ListIp Lists" */ '../views/apps/IpList/ListIpList'),
    ),
  },
];

export { adminRoutes, authRoutes, managerRoutes, analystRoutes };
